import {useEffect, useRef} from 'react';
import {useIntl} from 'react-intl';
import {convertFromHtmlToDraftJs} from './HtmlToDraftJsConverter';
import {convertFromDraftJsToHtml} from './DraftJsToHtmlConverter';
import {detectRoundTripDrift} from './roundTripDrift';
import {createToast} from '../toasts/another-toast/toaster';
import {
	type EditorSurface,
	trackEditorOpened,
	trackEditorSaveFailed,
	trackRoundTripDrift,
} from '../../../../tracking/amplitude/editorEvents';

type UseDraftJsHtmlConversionProps = {
	initialDraftJsContent: string;
	onDraftJsChanged?: (draftJsContent: string) => void;
	/**
	 * Which editor this is, for analytics. Omitted, the conversion still works and
	 * still reports failures to the user — only the events are skipped, because an
	 * event that cannot say which surface it came from cannot be acted on.
	 */
	surface?: EditorSurface;
	/**
	 * Reads the editor's own serialisation of its current content.
	 *
	 * Used once, before the user can type, to learn what an unedited blur looks like —
	 * see the save-attempt note in `convertHtmlToDraftJs`. Omitted, the conversion still
	 * works; a failed conversion is then reported whether or not anything changed, which
	 * is what it did before.
	 */
	getEditorHtml?: () => string | undefined;
};

type UseDraftJsHtmlConversionReturn = {
	initialHtml: string;
	convertHtmlToDraftJs: (htmlContent: string) => void;
	/**
	 * Whether `htmlContent` differs from what this editor held before the user could reach
	 * it — i.e. whether there is an edit worth committing.
	 *
	 * Only the imperative save asks. A blur commits unconditionally and lets the consumer
	 * decide, which is right there: a blur follows the user actually leaving the field. The
	 * close flush fires on every close, including the ones where nobody touched the
	 * description, and committing those is not free — the DraftJS round trip adds `data:{}`
	 * to every block, so a description written before this editor comes back different
	 * enough to slip past the consumer's unchanged-check and be rewritten. That is already
	 * true of any blur, and is what `detectRoundTripDrift` measures; this keeps the close
	 * from adding to it.
	 */
	hasUnexportedChange: (htmlContent: string) => boolean;
};

export const useDraftJsHtmlConversion = ({
	initialDraftJsContent,
	onDraftJsChanged,
	surface,
	getEditorHtml,
}: UseDraftJsHtmlConversionProps): UseDraftJsHtmlConversionReturn => {
	const intl = useIntl();
	const initialHtml = convertFromDraftJsToHtml(initialDraftJsContent);
	// Drift is a property of the stored content, so it cannot change between two blurs
	// of the same editor. Checking once keeps a second full round trip off every blur.
	const driftChecked = useRef(false);
	// Boxed so that "not captured yet" and "captured, and the editor gave us nothing"
	// stay distinguishable — the second has to stop the capture from running again on
	// every render, and only the first may retry.
	const unblurredHtml = useRef<{value: string | undefined} | null>(null);

	// One open per editor, not one per render. This is the denominator for every other
	// editor event: without it a fall in saves cannot be told apart from people simply
	// using the editor less.
	useEffect(() => {
		if (surface) {
			trackEditorOpened(surface);
		}
	}, [surface]);

	// What an unedited blur will hand back, captured before the user can reach the
	// editor. Only where blur is the save: elsewhere it is a serialisation per editor
	// mount for a string nothing reads, and a task modal mounts one editor per comment.
	//
	// One task later, not in this effect: Plate fills the editor from a state update of
	// its own, so the editor still serialises as empty while the effects of the render
	// that mounted it are running. Nobody can type inside that window, and if the
	// capture is somehow missed the baseline stays unset, which reports as before rather
	// than going quiet.
	useEffect(() => {
		if (!onDraftJsChanged || unblurredHtml.current) return;
		const capture = window.setTimeout(() => {
			unblurredHtml.current = {value: getEditorHtml?.()};
		}, 0);
		return () => window.clearTimeout(capture);
	}, [onDraftJsChanged, getEditorHtml]);

	const checkForDrift = () => {
		if (!surface || driftChecked.current) return;
		driftChecked.current = true;

		const drift = detectRoundTripDrift(initialDraftJsContent);
		if (drift) {
			trackRoundTripDrift(surface, drift);
		}
	};

	const convertHtmlToDraftJs = (htmlContent: string) => {
		checkForDrift();

		// Blur is the save only on the surfaces that gave us a callback to save through.
		// Both comment editors convert on submit instead (convertEditedHtml.ts), so the
		// result here would be discarded — and the footer composer is never read-only, so
		// it blurs on any click-away. Reporting from here toasted the user about a save
		// they had not asked for and then counted the real failure a second time when they
		// pressed Save.
		if (!onDraftJsChanged) return;

		// Whether this blur is a save attempt at all. The description mutation skips
		// unchanged content (description_section.editTaskDescription), so `Save Succeeded`
		// needs an edit while a conversion failure did not — clicking in and out of a
		// description that cannot be converted read as a run of lost saves against no
		// successes, which is the shape the release plan's wave-1 gate looks at.
		//
		// Compared against the editor's own serialisation rather than `initialHtml`: the
		// two are never equal, because Plate adds its own classes and drops what it cannot
		// model, and there is no DraftJS left to compare once conversion has thrown.
		// `getEditorHtml` and `onBlur` are the same function inside the design system, so
		// the two strings are comparable.
		const contentChanged = htmlContent !== unblurredHtml.current?.value;

		let draftJsContent: string;
		try {
			draftJsContent = convertFromHtmlToDraftJs(htmlContent);
		} catch {
			// The edit exists only in the editor at this point. Returning without telling
			// anyone is what used to happen, and it discarded the edit in silence: the save
			// callback below is what reaches CommitMutation, so its correlation-ID error
			// modal never fired either. The converter has already reported the throw with
			// the content shape; what is left is to say so where the user can see it, and
			// to leave their text in the editor where they can still copy it.
			//
			// Deliberately not rethrown: this runs from the editor's onBlur, so an escaping
			// throw would take out the surrounding tree instead of the one field.
			if (contentChanged) {
				createToast({
					duration: 8000,
					message: intl.formatMessage({id: 'rich_text_editor.changes_could_not_be_saved'}),
				});
				if (surface) {
					trackEditorSaveFailed(surface, 'conversion');
				}
			}
			return;
		}

		onDraftJsChanged(draftJsContent);
	};

	// A missing baseline means the capture below has not run yet, which is the first task
	// after mount — nobody can have typed inside it, so there is nothing to rescue. A
	// captured-but-unreadable baseline (`value === undefined`) reads as changed instead,
	// because losing an edit is worse than a write that changes nothing.
	const hasUnexportedChange = (htmlContent: string) =>
		unblurredHtml.current !== null && unblurredHtml.current.value !== htmlContent;

	return {initialHtml, convertHtmlToDraftJs, hasUnexportedChange};
};
